import React, { createContext, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react'
import useStorage from 'hooks/useStorage'
import { ordersInfo } from 'api/urls'
import { POST } from 'api/request'

const ORDER_KEY = 'order_id'

type OrderInfo = string | undefined
type OrderInfoContextType = [OrderInfo, Order | undefined, React.Dispatch<SetStateAction<OrderInfo>>]
interface Order {
  object: string
  id: string
  is_attached: boolean
  is_paid: boolean
  positions: OrderPosition[]
  total_cost: string
  real_id: number
}

interface OrderPosition {
  object: string
  id: string
  amount: number
  name: string
  description: string
  tax_rate: string
  gross_price: string
  net_price: string
  product: OrderProduct
  additional_profiles: number
  addons?: OrderAddon[]
  real_id: number
}

interface OrderProduct {
  object: string
  id: string
  producttype_id: string
  key: string
  name: string
  description: string
  real_id: number
}

interface OrderAddon {
  object: string
  id: string
  amount: number
  name: string
  description: any
  tax_rate: string
  gross_price: string
  net_price: string
  product: OrderProduct
  additional_profiles: number
  real_id: number
}

export const OrderInfoContext = createContext<OrderInfoContextType>(['', undefined, () => { }])

export const OrderInfoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [orderInfo, setOrderInfo] = useStorage(getOrderInfo(), ORDER_KEY, false)
  const [data, setData] = useState<Order | undefined>(undefined)

  const fetchDetails = async (id: string) => {
    try {
      const body = { orders: [id] }
      const response = await POST<{ data: Order[] }>(ordersInfo, { body })
      if (response.ok) {
        setData(response.data.data[0])
      } else {
        throw new Error('Wrong response')
      }
    } catch (error) {
      console.error('Fetch failed: ', error)
    }
  }

  useEffect(() => {
    if (orderInfo) fetchDetails(orderInfo)
  }, [orderInfo])

  return (
    <OrderInfoContext.Provider value={[orderInfo, data, setOrderInfo]}>
      {children}
    </OrderInfoContext.Provider>
  )
}

export const useOrderInfo = () => {
  const context = useContext(OrderInfoContext)
  if (!context) {
    throw new Error('useOrderInfo must be used within an OrderInfoProvider')
  }
  return context
}

const getOrderInfo = () => {
  const { search } = window.location
  if (!search || !search.includes(ORDER_KEY)) return
  const query = new URLSearchParams(search)
  const orderId = query.get(ORDER_KEY)
  if (orderId) {
    return orderId
  }
}
